import { useEffect, useState } from "react";
import "./style.css";
import { useUserInfo } from "../../../../context/user";
import DeleteFileModal from "../../../../components/DeleteFileModal";
import Uploadfile from "../../../../components/uploadFiles";
import AssistantMemory from "../../../../components/memory";
import env from "../../../../config/env"
import Plans from "../../../../components/Plans";
import NewAddWebsiteModal from "./component/NewAddWebsiteModal";
import toast from "react-hot-toast";
import { format } from "date-fns";
import ResetContext from "../ResetContext";
import SharedKnowledge from "./component/SharedKnowledge";
import FilterDropdown from "./component/FilterDropdown";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridCell,
} from "components/ui/data-grid";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  Search,
  FileText,
  Trash2,
  Download,
  Loader2,
  Link2,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useKnowledgeBaseFiles,
  useDeleteKnowledgeBaseFiles,
  useAssistantMemory,
  useUpdateAssistantMemory,
  useDeleteAssistantMemory,
  sharedKnowledgeKeys,
} from "../../../../queries/knowledgeBase";

const KB_TABS = ["Agent Knowledge", "Shared Knowledge", "Reset Context"];

function Files({ setActiveTab }) {
  const [activeKBTab, setActiveKBTab] = useState("Agent Knowledge");
  const BASE_URL = env.API_URL + "/v1";
  const userInfo = useUserInfo()[0];
  const userActiveLocation = useUserInfo()[4];
  const profileId = userActiveLocation?.id;
  const queryClient = useQueryClient();
  const [seletedFile, setSeletedFile] = useState();
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [selectPlan, setSelectPlan] = useState();
  const [selectedItems, setSelectedItems] = useState([]);
  const [openWebModal, setOpenWebModal] = useState(false);
  const [agentFilter, setAgentFilter] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleWebModal = () => setOpenWebModal(!openWebModal);

  const filesQuery = useKnowledgeBaseFiles(profileId, userInfo?._id, currentPage);
  const userFiles = filesQuery.data?.files;
  const pagination = filesQuery.data?.pagination || {};
  const deleteFilesMutation = useDeleteKnowledgeBaseFiles(profileId);
  const fileLoading = filesQuery.isFetching || deleteFilesMutation.isPending;

  const memoryQuery = useAssistantMemory(profileId);
  const memory = memoryQuery.data;
  const updateMemoryMutation = useUpdateAssistantMemory(profileId);
  const deleteMemoryMutation = useDeleteAssistantMemory(profileId);

  useEffect(() => {
    if (filesQuery.isError) {
      const error = filesQuery.error;
      toast.error(
        error?.message?.includes("400") ? "Files not found" : error.message
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesQuery.isError, filesQuery.error]);

  const refreshFilesForActiveTab = () => {
    if (activeKBTab === "Shared Knowledge") {
      queryClient.invalidateQueries({ queryKey: sharedKnowledgeKeys.list(profileId) });
    } else {
      filesQuery.refetch();
    }
  };

  // updateMemory/deleteMemory are handed to <AssistantMemory> as plain async
  // functions that never throw (it awaits them with no try/catch) — matching
  // the original raw-axios versions, just backed by mutations now.
  const updateMemory = async (content) => {
    try {
      return await updateMemoryMutation.mutateAsync(content);
    } catch (error) {
      return error?.response?.data;
    }
  };

  const deleteMemory = async () => {
    try {
      return await deleteMemoryMutation.mutateAsync();
    } catch (error) {
      return error;
    }
  };

  const refreshHandler = () => {
    filesQuery.refetch();
    memoryQuery.refetch();
  };

  const confirmDelete = (fileInfo) => {
    setSeletedFile(fileInfo);
  };

  const deleteFile = async (fileIdsToDelete) => {
    const idsToDelete = seletedFile ? [seletedFile?.id] : fileIdsToDelete;

    try {
      await deleteFilesMutation.mutateAsync(idsToDelete);
      setSeletedFile();
      setSelectedItems([]);
    } catch (error) {
      alert(error.response?.data?.err || "An error occurred during deletion.");
    }
  };

  const applyFilters = (list) =>
    (list || []).filter((item) => {
      if (agentFilter && item.assistant_name !== agentFilter) return false;
      if (fileTypeFilter && item.name !== fileTypeFilter) return false;
      return true;
    });

  const filteredData = Array.isArray(userFiles)
    ? userFiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  const totalPages = pagination?.total || 0
  const maxButtonsToShow = 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = applyFilters(searchQuery ? filteredFiles : filteredData)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startPage = Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1);
  const endPage = Math.min(startPage + maxButtonsToShow - 1, totalPages);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = userFiles.filter((file) => {
      const fileName = file.name ? file.name.toLowerCase() : "";
      const fileUsername = file.username ? file.username.toLowerCase() : "";
      return fileName.includes(query) || fileUsername.includes(query);
    });

    setFilteredFiles(filtered);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (itemId) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        return [...prevSelected, itemId];
      }
    });
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      return;
    }
    deleteFile(selectedItems);
    setSelectedItems([]);
  };

  const handleDownloadFile = async (fileItem) => {
    setDownloadingFileId(fileItem.id);
    try {
      const win = window.open(`${BASE_URL}/chat/assistants/files/download?file_id=${fileItem?.id}&isStreaming=true`, '_blank')
      setTimeout(() => win.close(), 3000)
      setDownloadingFileId(null);
    } catch (error) {
      console.error("Error downloading file:", error.message);
      setDownloadingFileId(null);
    }
  };

  return (
    <div className="container-alignment">
      <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
        <span className="page-eyebrow">Configure</span>
        <h1 className="page-display">
          Knowledge Base. <span className="accent block md:inline">Context your Tasks can draw on.</span>
        </h1>
        <p className="page-lede">
          Drop in decks, playbooks, or links. For spreadsheets and reports, use the Data Interpreter →
        </p>
      </div>

      <div className="flex gap-8 border-b border-border mt-5 mb-6">
        {KB_TABS.map((tab) => {
          const isActive = activeKBTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveKBTab(tab)}
              className={`bg-transparent border-none px-1 py-3 text-[15px] cursor-pointer -mb-px ${
                isActive
                  ? "text-foreground font-semibold border-b-2 border-foreground"
                  : "text-muted-foreground font-normal border-b-2 border-transparent"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {activeKBTab === "Reset Context" && <ResetContext />}

      {activeKBTab === "Shared Knowledge" && (
        <SharedKnowledge
          onUpload={refreshHandler}
          onAddUrl={handleWebModal}
        />
      )}

      {activeKBTab === "Agent Knowledge" && (
        <>
          <div>
            <p className="text-[18px]">Assign knowledge to one or more agents.</p>
            <p className="text-[16px] text-muted-foreground">Only selected agents will have access.</p>
          </div>
          <div className="first_box relative">
            <div className="search_box relative">
              <Search className="absolute top-3 left-3 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                className={`!pl-9 ${memory ? "!w-[500px]" : "!w-[740px]"}`}
                placeholder="Search for Files"
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>

            <div style={{ display: "flex", width: "50%", justifyContent: "end" }}>
              {memory && (
                <AssistantMemory
                  classname="filecogChatBtn"
                  refreshchat={memoryQuery.refetch}
                  updateMemory={updateMemory}
                  gettingmemory={memory}
                  refreshmemory={refreshHandler}
                  deleteMemory={deleteMemory}
                />
              )}
              <Uploadfile
                classname="filecogChatBtn"
                refreshchat={refreshHandler}
              />
              <Button
                variant="outline"
                className="ml-5"
                onClick={handleWebModal}
                aria-label="Add URL"
              >
                <Link2 className="w-4 h-4" />
                <span>Add URL</span>
              </Button>
            </div>
          </div>

          <p style={{ fontWeight: 600, marginBottom: "8px", fontSize: "14px" }}>
            Filter by:
          </p>
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
            <FilterDropdown
              value={agentFilter}
              onChange={setAgentFilter}
              placeholder="All agents"
              options={[
                { value: "", label: "All agents" },
                ...Array.from(
                  new Set(
                    (userFiles || [])
                      .map((f) => f.assistant_name)
                      .filter(Boolean)
                  )
                ).map((name) => ({ value: name, label: name })),
              ]}
            />
            <FilterDropdown
              value={fileTypeFilter}
              onChange={setFileTypeFilter}
              placeholder="All files"
              options={[
                { value: "", label: "All files" },
                ...Array.from(
                  new Set(
                    (userFiles || []).map((f) => f.name).filter(Boolean)
                  )
                ).map((name) => ({ value: name, label: name })),
              ]}
            />
          </div>

          <Button
            variant="outline"
            onClick={handleDeleteSelected}
            disabled={selectedItems.length === 0}
          >
            <Trash2 className="w-4 h-4" />
            Delete All
          </Button>

          <div>
            <TooltipProvider>
            <DataGrid
              columns="auto minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) auto"
              minWidth={900}
            >
              <DataGridHeader>
                <DataGridCell className="text-muted-foreground" />
                <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">
                  File Name
                </DataGridCell>
                <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">
                  Agents
                </DataGridCell>
                <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">
                  Date Created
                </DataGridCell>
                <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">
                  Uploaded By
                </DataGridCell>
                <DataGridCell className="text-muted-foreground" />
              </DataGridHeader>

              {fileLoading ? (
                <div className="py-4 text-center text-foreground">
                  Loading Files...
                </div>
              ) : currentItems?.length > 0 ? (
                currentItems.map((item, index) => (
                  <DataGridRow key={index} className="bg-card">
                    <DataGridCell className="overflow-visible">
                      <Checkbox
                        className="w-[18px] h-[18px] cursor-pointer"
                        aria-label={`Select ${item.name}`}
                        checked={selectedItems.includes(item.id)}
                        onCheckedChange={() => handleCheckboxChange(item.id)}
                      />
                    </DataGridCell>

                    <DataGridCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center">
                            <span className="pr-2">
                              <FileText className="w-4 h-4" />
                            </span>
                            {item.name.length > 15
                              ? item.name.slice(0, 15) + "..."
                              : item.name}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {item.name}
                        </TooltipContent>
                      </Tooltip>
                    </DataGridCell>

                    <DataGridCell>{item.assistant_name}</DataGridCell>

                    <DataGridCell>
                      {format(new Date(item?.created_at), "dd/MM/yyyy")}
                    </DataGridCell>
                    <DataGridCell>{item.username}</DataGridCell>
                    <DataGridCell className="overflow-visible text-right">
                      <div className="flex justify-end items-center">
                        <button
                          type="button"
                          aria-label="Download file"
                          onClick={() => {
                            handleDownloadFile(item);
                          }}
                          disabled={
                            downloadingFileId !== null &&
                            downloadingFileId !== item.id
                          }
                          className="flex items-center mr-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
                        >
                          {downloadingFileId === item.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Download className="w-5 h-5" />
                          )}
                        </button>

                        <button
                          type="button"
                          aria-label="Delete file"
                          onClick={() => confirmDelete(item)}
                          className="bg-transparent border-none cursor-pointer text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </DataGridCell>
                  </DataGridRow>
                ))
              ) : (
                <div className="py-4 text-center text-foreground">
                  No files found.
                </div>
              )}
            </DataGrid>
            </TooltipProvider>
          </div>

          {/* Pagination Controls */}
          {!fileLoading ? (
            <div className="flex justify-center mt-5">
              <Button
                variant="outline"
                className="mx-[5px]"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {[...Array(endPage - startPage + 1)].map((_, index) => {
                const pageNumber = startPage + index;
                return (
                  <Button
                    key={pageNumber}
                    variant={pageNumber === currentPage ? "default" : "outline"}
                    className="mx-[5px]"
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                className="mx-[5px]"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          ) : (
            ""
          )}
        </>
      )}

      {selectPlan !== "" ? (
        <Plans
          justCloseIt={() => setSelectPlan(false)}
          isModalOpen={selectPlan ? true : false}
        />
      ) : (
        ""
      )}

      {seletedFile !== "" ? (
        <DeleteFileModal
          isModalOpen={seletedFile ? true : false}
          yesDelete={deleteFile}
          justCloseIt={() => {
            setSeletedFile(false);
          }}
          fileInfo={seletedFile}
        />
      ) : (
        ""
      )}

      {openWebModal && (
        <NewAddWebsiteModal
          isOpen={openWebModal}
          onClose={handleWebModal}
          getuploadfiles={refreshFilesForActiveTab}
          isSharedKnowledge={activeKBTab === "Shared Knowledge"}
        />
      )}
    </div>
  );
}

export default Files;
